import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import '../App.css';
import AlertMessage from '../AlertMessage';
import PanelDetails from '../panel/PanelDetails';
import RemoveRequisition from '../requisition/RemoveRequisition';
import withWorkflow from '../components/withWorkflow';
import PanelCustomization from '../panel/PanelCustomization';
import Modal from '../components/Modal';
import { fetchOmsRequisitions, submitOrderConfirmationAndPayment } from '../util/apiUtils';
import { validateCreditCardForm, validateCreditCardField } from '../payment/CreditCardForm';
import { isCanadianClient, isDomesticRequisitionType } from '../util/gearOrderUtil';
import ExpeditedShippingPayment from '../payment/ExpeditedShippingPayment';
import RecommendedProducts from '../recommended-products/RecommendedProducts';

/**
 * Component for the 'Confirmation' step of a ship to office (bulk order) GEAR order.
 */
class ShipToOfficeConfirmation extends Component {
	/**
	 * Constructor - Set up initial state and bind functions
	 */
	constructor(props) {
		super(props);

		this.showNoRequisitionsError = this.showNoRequisitionsError.bind(this);
		this.removeRequisitionResponseHandler = this.removeRequisitionResponseHandler.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.goBack = this.goBack.bind(this);
		this.errorRef = null;
		this.recommendedProductRef = React.createRef();

		/* An array of timeouts used when updating requisition quantities. See this.onQuantityChange() */
		this.quantityTimers = [];

		const step = this.props.getWorkflowStep('practitioner_confirmation');
		const confirmed = step && step.completed;
		const quantityMap = this.props.gearOrder.requisitions.map( req => ({ gearReqId: req.id, quantity: req.quantity}));

		this.state = {
			displayRemoveRequisition: false, // Toggle the delete requisition modal
			saveDisabled: !this.props.gearOrder.shippingInformation, // Toggles the "Next" button
			confirmed: confirmed, // Whether this gear order has been confirmed (This step has been previously completed)
			omsRequisitions: [], // List of oms requisition structures for this order's gear requisitions
			showCustomizeOverlay: null, // When not null, shows the customization overlay for a gear requisition
			customizationOverlayStatus: null, // A status message to show while a modal is waiting to load an oms requisition
			customizationSaved: null, // Used to show a confirmation after customizations are saved
			quantityMap: quantityMap, // A list of quantities for gear requisitions. May include a status after updating
			reqOverCanadaQtyLimit: null, // The reqId that triggered a Canada quantity limit warning
			showError: false,
			errorMessage: null,
			paymentDetails: !this.props.gearOrder.expeditedShippingCharge
				? null
				: {
						cardNo: {
							value: '',
							valid: null,
							message: null
						},
						cardCvv: {
							value: '',
							valid: null,
							message: null
						},
						cardExpDate: {
							value: '',
							valid: null,
							message: null
						},
						cardholderName: {
							value: '',
							valid: null,
							message: null
						},
						cardZipCode: {
							value: '',
							valid: null,
							message: null
						},
						tokenId: { 
							value: this.props.gearOrder?.practitioner?.shippingCardOnFile ? '9999A' : ''
						}
				  }
		};
	}

	/**
	 * When this component first mounts, load the oms requisition structures needed to customize the order's gear requisitions.
	 */
	componentDidMount() {
		if (this.props.gearOrder.requisitions.length === 0) {
			this.showNoRequisitionsError();
			this.setState({saveDisabled: true});
		}

		if (this.props.gearOrder.requisitions.length > 0) {
			let requisitionIdsParam = this.props.gearOrder.requisitions
				// Only fetch req structure for requisitions with customizations
				.filter(req => Number(req.panel) !== 0 || (req.components && req.components.length > 0) || (req.addons && req.addons.length > 0))
				.map(requisition => requisition.id);

			if (requisitionIdsParam.length === 0) {
				return;
			}

			this.props.setLoading(true);
			fetchOmsRequisitions( requisitionIdsParam )
			.then(json => {
				this.setState({omsRequisitions: json});
			}).catch(e => {
				let errorMessage = e.message;
				if( !errorMessage ) {
					errorMessage = 'An unexpected error has occurred. Please call us at 800-522-4762.';
				}
				this.props.showError(e.message);
			}).finally(() => {
				this.props.setLoading(false);
			});
		}
	}

	/**
	 * When this component is updated check if all gear requisitions have been removed. If so, show the No Requisition message.
	 * This could happen if all requisitions are deleted from the order.
	 *
	 * @param prevProps The old properties from before the update
	 */
	componentDidUpdate(prevProps) {
		if (prevProps.gearOrder.requisitions.length !== this.props.gearOrder.requisitions.length && this.props.gearOrder.requisitions.length === 0) {
			this.showNoRequisitionsError();
		}

		// Scroll to the newly added recommended product well
		if (this.recommendedProductRef?.current) {
			window.scrollTo({ top: this.recommendedProductRef.current.offsetTop - 150, behavior: 'smooth' });
			// Clear the ref so other updates don't keep scrolling to this component
			this.recommendedProductRef.current = null;
		}
	}

	/*
	 * Update state to show the No Requisitions message.
	 */
	showNoRequisitionsError() {
		this.setState({
			showError: true, 
			errorMessage: <span>Information cannot be confirmed until at least one requisition is added to the order. 
				Click <Link to={"/order/" + this.props.gearOrder.id + "/panels"}>here</Link> to add a requisition.</span>
		});
	}

	/**
	 * Shows the delete requisition modal when a delete icon is clicked.
	 *
	 * @param the DOM event that triggered the click
	 */
	onDelete = (event) => {
		this.setState({
			displayRemoveRequisition: true,
			requisitionToRemove: this.props.gearOrder.requisitions.find(req => req.id === Number(event.currentTarget.dataset.gearreqid))
		});
	}

	/**
	 * Handles the response from removing a requisition from an order.
	 *
	 * @param response the fetch response
	 */
	removeRequisitionResponseHandler(response) {
		switch (response.status) {
			case 200:
				response.json().then(json => {
					this.props.setGearOrder(json);
					if (json.requisitions.length === 0) {
						this.props.setOmsRequisition(null);
						this.props.setActiveGearRequisitionId(null);
						this.setState({saveDisabled: true});
					}
				});
				break;
			case 400:
				this.props.showError("Sorry, this requisition cannot be removed");
				break;
			case 403:
				this.props.showError("Sorry, you are not authorized to remove this requisition");
				break;
			case 404:
				this.props.showError("Sorry, this requisition cannot be found");
				break;
			default:
				this.props.showError("Sorry, an unexpected error has occcured. If you continue to receive this message, please contact us.");
		}
	}

	/**
	 * Validate payment input field changes and update state
	 * @param event The HTML event that triggered this method
	 */
	handlePaymentInputChange = (event) => {
		const target = event.target;
		const value = target.type === 'checkbox' ? target.checked : target.value;
		const valid = target.type === 'checkbox' ? true : !!value || false;
		const fieldName = target.name;

		let newField = {
			value: value,
			valid: valid
		};

		// Only validate when the value is empty. We don't want to do other validation like credit card length
		// when typing in the field
		newField.message = value ? '' : validateCreditCardField(fieldName, newField).message;

		this.setState((prevState) => {
			return {
				...prevState,
				paymentDetails: {
					...prevState.paymentDetails,
					[fieldName]: newField
				}
			}
		});
	};

	goBack(e) {
		e.preventDefault();
		this.props.updateStep(this.props.gearOrder, 'practitioner_confirmation', 'Back');
	}

	editShipping = () => {
		this.props.updateStep(this.props.gearOrder, 'shipping', null, null)
	}

	editPanels = () => {
		this.props.updateStep(this.props.gearOrder, "panel", null, null);
	}

	handleSubmit(event) {
		event.preventDefault();
		if (this.state.confirmed) {
			this.props.updateStep(this.props.gearOrder, 'practitioner_confirmation', 'Next');
		} else {
			if (this.props.gearOrder.expeditedShippingCharge && !this.props.gearOrder.expeditedShippingPayment) {
				this.setState(
					{
						paymentDetails: validateCreditCardForm(this.state.paymentDetails)
					},
					() => {
						if (this.state.paymentDetails?.tokenId?.valid ||
							Object.values(this.state.paymentDetails).every((prop) => prop.valid)) {
							this.confirmInfoAndSubmitPayment();
						}
					}
				);
			} else {
				this.confirmInfoAndSubmitPayment();
			}
		}
	}

	/**
	 * Submit the payment info, if needed, and confirm the order.
	 */
	confirmInfoAndSubmitPayment = () => {
		this.setState({ saveDisabled: true });
		this.props.setLoading(true);

		const gearOrderId = this.props.gearOrder.id;

		let orderPayment;
		if (this.props.gearOrder.expeditedShippingCharge && !this.props.gearOrder.expeditedShippingPayment) {
			orderPayment = {
				payments: [
					{
						paymentType: 'expeditedShipping',
						paymentFrom: this.props.userInfo?.type,
						cardNo: this.state.paymentDetails.cardNo.value,
						cardExpDate: this.state.paymentDetails.cardExpDate.value,
						cardCvv: this.state.paymentDetails.cardCvv.value,
						cardholderName: this.state.paymentDetails.cardholderName.value,
						cardZipCode: this.state.paymentDetails.cardZipCode.value,
						tokenId: this.state.paymentDetails.tokenId?.value,
						saveForLater: !!this.state.paymentDetails.saveForLater?.value,
						cardAuthAmount: this.props.gearOrder.expeditedShippingCharge,
						fullPaymentAmount: this.props.gearOrder.expeditedShippingCharge,
						gearReqId: null,
						gearOrderId: gearOrderId
					}
				]
			}
		}

		submitOrderConfirmationAndPayment(this.props.gearOrder.id, orderPayment)
			.then((json) => {
				if (json.error) {
					return Promise.reject(json.error);
				} else if (json.gearOrder?.id) {
					this.props.setGearOrder(json.gearOrder);
					this.props.setDirty(false);
					this.props.updateStep(json.gearOrder, 'practitioner_confirmation', 'Next');
					return;
				} else {
					return Promise.reject({});
				}
			})
			.catch((e) => {
				let errorMessage;
				if (e.error === 'ExpeditedShippingPaymentException') {
					errorMessage = e.message || '';
					if (!errorMessage) {
						errorMessage = (
							<>
								An unexpected error occurred when submitting your payment. Please call us at
								800-522-4762 or contact <a href="/mygdx/contact">Customer Support</a> for help.
							</>
						);
					}
				}

				let errorEl;
				if (errorMessage) {
					errorEl = <span>{errorMessage}</span>;
				} else {
					errorEl = (
						<span>
							An unexpected error occurred when submitting your order. Please call us at 800-522-4762 or contact{' '}
							<a href="/mygdx/contact">Customer Support</a>.
						</span>
					);
				}

				this.setState({
					showError: true,
					errorMessage: errorEl,
					saveDisabled: false
				});
				// Scroll up to the error message (+ 200 is to account for the page header)
				window.scrollTo(0, this.errorRef.offsetTop + 200);
			})
			.finally(() => {
				this.props.setLoading(false);
			});
	}

	/**
	 * Update state to show the test selection custimzation modal for the given orderable item.
	 * Also clears out previous customization selections or messages (Only one req can be customized at a time).
	 * Fetch the required oms requisition object if it has not already been loaded.
	 *
	 * @param gearReqId The ID of the gear requisition being customized.
	 * @param orderableItemId The ID of the orderable item for which to display the custimzation modal.
	 * @param omsRequisition The oms requisition structure. If this is null we will fetch it.
	 */
	showCustomizeOverlay = (gearReqId, orderableItemId, omsRequisition) => {
		this.setState({
			showCustomizeOverlay: orderableItemId,
			customizedSelections: null
		});

		// Clear the customization saved message when loading a customization moal
		if( gearReqId !== null ) {
			this.setState({
				customizationSaved: null
			});
		}

		if( omsRequisition || !gearReqId || !orderableItemId ) {
			return;
		}

		this.setState({customizationOverlayStatus: <i className="fa fa-spinner fa-spin fa-fw" style={{"fontSize": "5em"}}></i>});
		fetchOmsRequisitions( [gearReqId] )
		.then(json => {
			this.setState({
				omsRequisitions: this.state.omsRequisitions.concat(json),
				customizationOverlayStatus: null
			});
		}).catch(e => {
			let errorMessage = e.message;
			if( !errorMessage ) {
				errorMessage = 'An unexpected error has occurred. Please call us at 800-522-4762.';
			}
			this.setState({customizationOverlayStatus: <span className='text-danger'>{errorMessage}</span>});
		});
	}

	/**
	 * Stores the customization details anytime a selection is made in the customization modal.
	 *
	 * @param selections The selected customizations object. This object contains the following:
	 *						gearRequisitionId - The ID of the gear requisition being customized
	 *						orderableItemId - The ID of the orderable item being customized
	 *						selectedPanel - The selected panel for the requisition
	 *						selectedComponents - The selected panel components for the requisition
	 *						selectedAddOns - The selected panel addons for the requisition
	 *						patientInstructions - The patientInstructions for the requisition
	 */
	onSelection = (selections) => {
		this.props.setDirty(true);
		this.setState({ customizedSelections: selections });
	}

	/**
	 * Find and update the appropriate gear requistion with the customization selections and call the back-end to
	 * save the changes.
	 */
	onCustomize = () => {
		this.props.setDirty(false);
		if( !this.state.customizedSelections ) {
			return;
		}

		let reqToUpdate = this.props.gearOrder.requisitions.find( req => {
			if( this.state.customizedSelections.gearRequisitionId ) {
				return req.id === this.state.customizedSelections.gearRequisitionId;
			} else if( this.state.customizedSelections.orderableItemId ) {
				return req.omsOrderableItemId === this.state.customizedSelections.orderableItemId;
			}
			return false;
		});

		// Make a copy of the existing req and update the customizable values
		/*
		 * SCR-7322:
		 * Remove the addOnOptions and componentOptions, letting the back-end fall back to addons and components.
		 * This is a short-cut so we don't have to worry about updating the options lists on the front-end to match
		 *  the selections (This works because bulk shipment does not support CPT Quantity changes).
		 */
		const updatedReq = {...reqToUpdate, 
			'addons': this.state.customizedSelections.selectedAddOns, 
			'addOnOptions': null,
			'components': this.state.customizedSelections.selectedComponents, 
			'componentOptions': null,
			'panel': this.state.customizedSelections.selectedPanel,
			'patientInstructions': this.state.customizedSelections.patientInstructions
		};

		// Persist the changes
		this.updateRequisition( updatedReq );
	}

	/**
	 * Send customization data to the back-end to be saved.
	 * When the save completes, set the customizationSaved state value with the requisition ID so a confirmation
	 * message gets displayed.
	 * Also call this.props.setGearOrder with the returned gearOrder so the updates will be applied in any component
	 * using the gearOrder.
	 *
	 * @param requisition The updated gear requisition to save
	 */
	updateRequisition = (requistion) => {
		fetch(process.env.REACT_APP_GEAR_REST_URL+'/customizeTestSelections/', {
			method: 'PUT',
			credentials: 'include',
			mode: 'cors',
			timeout:1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(requistion)
		})
		.then(response => {
			if (response.ok) {
				return response.json();
			} else {
				return Promise.reject({response: response});
			}
		})
		.then(json => {
			if (json && json.id){
				this.props.setGearOrder(json);
				this.setState({customizationSaved: requistion.id});
			}
		})
		.catch(error => { 
			this.props.showError("The network is currently unavailable. Please call us at 800-522-4762.");
		});
	}

	/**
	 * The event handler for when a quantity input changes.
	 * Update the state's quantityMap with the new quantity value and start a timeout that will persist the change.
	 * A timeout is used so we don't make extra calls to the database. For example, if the input's 'up' arrow is being
	 * clicked many times to update the count we don't want to save each iteration. Instead, we wait until there is a
	 * pause in the input changes before saving the value.
	 *
	 * @param event the HTML change event. The event's target will contain the gearReqId and new quantity value
	 */
	onQuantityChange = (event) => {
		const gearReqId = Number(event.target.dataset.gearreqid);
		let quantity = Number(event.target.value) || 1;
		let originalQuantity = this.state.quantityMap.find(req => req.gearReqId === gearReqId).quantity;

		const newQuantityMap = this.state.quantityMap.map(req => (req.gearReqId === gearReqId ? { gearReqId, quantity } : req));

		// SCR-8019: Limit Canada orders to <= 25 total packs
		let reqOverCanadaQtyLimit = null;
		if (isCanadianClient(this.props.gearOrder)) {
			const totalQuantity = newQuantityMap.reduce((sum, i) => {
				let itemQuantity = +(i.quantity || 0);
				return sum + itemQuantity;
			}, 0);
			if (totalQuantity > 25) {
				// Find item being updated and set quantity to (25 - other quantities)
				const itemBeingUpdated = newQuantityMap.find((i) => i.gearReqId === gearReqId);
				if (itemBeingUpdated) {
					const maxQuantityAvailableForItem =
						25 -
						newQuantityMap
							.filter((i) => i.gearReqId !== gearReqId)
							.reduce((sum, i) => {
								let itemQuantity = +(i.quantity || 0);
								return sum + itemQuantity;
							}, 0);
					itemBeingUpdated.quantity = maxQuantityAvailableForItem;
					quantity = maxQuantityAvailableForItem;
				}
				// Set flag to show message
				reqOverCanadaQtyLimit = gearReqId;
			}
		}

		this.setState({ quantityMap: newQuantityMap, reqOverCanadaQtyLimit: reqOverCanadaQtyLimit });

		// Don't do anything when the quantity hasn't changed (Could happen when invalid input is entered or when the Canada limit has been reached)
		if( quantity === originalQuantity ) {
			return;
		}

		/* An array of timeouts used when updating requisition quantities.
		 * A timeout is used so that many quick updates (like typing in the input) will get bundled
		 * into a single back-end call.
		 */
		let timer = this.quantityTimers.find( t => t.gearReqId === gearReqId );
		if( timer && timer.timer ) {
			clearTimeout( timer.timer );
		}
		this.quantityTimers = this.quantityTimers
			.filter(t => t.gearReqId !== gearReqId );

		/* Create a new timeout that will save the changes after 750 ms.
		 * This timeout may be cancled if another change for the same gearReqId happens before the 750ms timeout.
		 */
		this.quantityTimers.push({
				gearReqId,
				timer: setTimeout(() => {
					// Save the update
					this.updateQuantity( {gearReqId, quantity} );
					
					// Clear this timer from the timers object
					this.quantityTimers = this.quantityTimers
						.filter(t => t.gearReqId !== gearReqId );
				},750)
			});
	}

	/**
	 * Persist the quantity update of a gearReq.
	 *
	 * @param gearReqId The ID of the gear requisition to be updated
	 * @param quantity The new quantity to set on the requisition
	 */
	updateQuantity = ( {gearReqId, quantity} ) => {
		// Set an 'updating' status on the quantityMap's object we are updating. The status is used for UI notifications.
		// The status will be updated to 'saved' or 'error' depending on the fetch result
		const newQuantityMap = this.state.quantityMap.map(req => (req.gearReqId === gearReqId ? {gearReqId, quantity, 'status': 'updating'} : req));
		this.setState({quantityMap: newQuantityMap});
		
		fetch(process.env.REACT_APP_GEAR_REST_URL + `/gear-requisition/${gearReqId}/quantity?quantity=${quantity}`, {
			mode: 'cors',
			credentials: 'include',
			method: 'PUT',
			timeout: 1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		}).then(response => {
			if (response.ok) {
				return response.json();
			} else {
				const newQuantityMap = this.state.quantityMap.map(req => (req.gearReqId === gearReqId ? {gearReqId, quantity, 'status': 'error'} : req));
				this.setState({quantityMap: newQuantityMap});
			}
		}).then(json => {
			if( json && json.id ) {
				this.props.setGearOrder( json );
				const newQuantityMap = this.state.quantityMap.map(req => (req.gearReqId === gearReqId ? {gearReqId, quantity, 'status': 'saved'} : req));
				this.setState({quantityMap: newQuantityMap});
			}
		}).catch(e => {
			const newQuantityMap = this.state.quantityMap.map(req => (req.gearReqId === gearReqId ? {gearReqId, quantity, 'status': 'error'} : req));
			this.setState({quantityMap: newQuantityMap});
		});
	}

	/**
	 * This method is called when the user is finished adding a recommended product to the order.
	 *
	 * Update the state's quantityMap with the new requisition and push the updated Gear Order to
	 * the parent. The result will be a new requisition well is displayed for the new product.
	 *  
	 * @param gearOrder The updated Gear Order after a new requisition has been saved
	 * @param gearReqId The ID of the new GearRequisition added to the order
	 */
	loadSavedRecommendedProduct = (gearOrder, gearReqId) => {
		const quantityMap = gearOrder.requisitions.map(req => ({ gearReqId: req.id, quantity: req.quantity }));

		// Keep track of the newly added GearReq so we can highlight the new requisition well
		this.setState(prevState => {
			return {
				quantityMap: quantityMap,
				recommendedProductGearReqIds: [...(prevState.recommendedProductGearReqIds || []), gearReqId]
			}
		}, () => {
			this.props.setGearOrder(gearOrder);
		});
	}

	/**
	 * Creates the JSX component for shipping information
	 *
	 * @param gearOrder The gear order
	 * @return JSX Shipping component
	 */
	buildShippingInfoDisplay = (gearOrder) => {
		const isInternational = !isDomesticRequisitionType(gearOrder);

		if (!gearOrder.shippingInformation) {
			return (
				<div className="well">
					<h3>
						<div>
							Shipping Information
							<div className="separator-2"></div>
						</div>
					</h3>
					<AlertMessage type="danger">
						<strong>Missing Shipping Information.
							Click <Link to={"/order/" + gearOrder.id + "/shipping"}>here</Link> to add it.
						</strong>
					</AlertMessage>
				</div>
			);
		}

		const shippingInfo = gearOrder.shippingInformation;
		let shippingMethod = gearOrder.shippingMethod + ' - $' + gearOrder.expeditedShippingCharge;
		
		let instructions = '';
		if (shippingInfo.instructions && shippingInfo.instructions.length > 0) {
			instructions = shippingInfo.instructions;
		}

		return (<div className="well">
			<h3>
				<div>
					Shipping Information
					{ !gearOrder.workflow.practitionerConfirmed && 
							<button type="button" title="Edit Shipping Information" className="btn btn-edit btn-primary" onClick={this.editShipping}>Edit</button>
					}
					<div className="separator-2"></div>
				</div>
			</h3>
			<div className="row">
				<div className="col-xs-8">
					{!isInternational && <p>{shippingMethod}</p>}
					<address>
						{
							(shippingInfo.company || shippingInfo.attention) && (<span>
								<strong>{shippingInfo.company + (shippingInfo.attention ? ' - Attn: ' + shippingInfo.attention : '')}</strong>
								<br/>
							</span>)
						}
						{shippingInfo.address1 + (shippingInfo.address2 ? ', ' + shippingInfo.address2 : '')}<br/> {shippingInfo.city + ', ' + shippingInfo.state + ' ' + shippingInfo.zipCode}
					</address>
					<p className={instructions.length > 0 ? "" : "hidden"}><strong>Instructions: </strong> {instructions}</p>
				</div>
			</div>

		</div>);
	}
		

	/**
	 * Creates the JSX component for a gear requisition.
	 * This component displays the requisition name, quantity and test selections as well as the controls 
	 * to update/customize the values.
	 *
	 * @param requisition The gear requisition
	 * @return JSX requisition component
	 */
	buildRequisitionDisplay = (requisition) => {
		let omsRequisition = this.state.omsRequisitions ? this.state.omsRequisitions.find(omsRequisition => {
			if (omsRequisition.id === requisition.reqNumber) {
				return true;
			} else {
				let omsReqOrderableItemId = (omsRequisition.orderableItem || {}).id;
				return requisition.omsOrderableItemId && Number(omsReqOrderableItemId) === Number(requisition.omsOrderableItemId);
			}
		}) : null;

		let requisitionTitle = requisition.omsOrderableItemName;
		const quantityMap = this.state.quantityMap.find(req => req.gearReqId === requisition.id);
		const quantity = quantityMap.quantity;
		let quantityStatus;
		if( quantityMap.status ) {
			if (quantityMap.status === 'updating') {
				quantityStatus = <small><span className="ml-10 text-info">Saving... <i className="fa fa-spinner fa-spin fa-fw"></i></span></small>;
			} else if (quantityMap.status === 'saved') {
				quantityStatus = <small><span className="ml-10 text-success">The quantity has been updated. <i className="fa fa-check-circle"></i></span></small>;
			} else if (quantityMap.status === 'error') {
				quantityStatus = <span className="ml-10 text-danger"><i className="fa fa-exclamation-circle"></i> An unexpected error occurred while updating the quantity.</span>;
			}
		}

		const hasCustomization = Number(requisition.panel)
			|| (requisition.components && requisition.components.length)
			|| (requisition.addons && requisition.addons.length)
			|| requisition.patientInstructions;
		
		const isRecommendedProduct = this.state.recommendedProductGearReqIds?.includes(requisition.id);

		return (<div className={'well' + (isRecommendedProduct ? ' recommended-product' : '')} key={requisition.id} ref={isRecommendedProduct ? this.recommendedProductRef : null}>
			<div className="confirmation-well-header">
				<h3 className="row">
					<div className="col-xs-8">
						{requisitionTitle}
						<div className="separator-2"></div>
					</div>
					<div className="col-xs-4 text-right">
						{!this.props.gearOrder.workflow.practitionerConfirmed &&
							<span>
								<button type="button" title="remove" className="btn btn-edit btn-danger" data-gearreqid={requisition.id} onClick={this.onDelete}>Delete</button>
							</span>
						}
					</div>
				</h3>
			</div>
			<div className='space-bottom'>
				<h4>Quantity</h4>
					<div className="form-inline">
						<div className="form-group">
							{this.state.confirmed && quantity }
							{!this.state.confirmed &&
								<input
									type="number" 
									className="form-control" 
									id={'quantity-input-' + requisition.id} 
									data-gearreqid={requisition.id} 
									placeholder="1" 
									min='1' 
									max='99' 
									value={quantity} 
									onChange={this.onQuantityChange} 
									onFocus={(event)=>event.target.select()} 
									style={{'width': '50px', 'padding': '6px'}}/>
							}
						</div>
						{quantityStatus}
					</div>
					{(requisition.id === this.state.reqOverCanadaQtyLimit) && (
						<div className="text-danger mt-10">
							<i className="fa fa-fw fa-warning" aria-hidden="true"></i>  A maximum of 25 collection 
							packs may be ordered online. If you need to order more packs, please call us at 800-522-4762.
						</div>
					)}
			</div>
			
			<h4>Test Selection 
				{!this.state.confirmed && 
					<button type="button" title="Edit Test Selections" className="btn btn-edit btn-primary" onClick={() => this.showCustomizeOverlay(requisition.id, requisition.omsOrderableItemId, omsRequisition)}>Edit</button>
				}
				{(this.state.customizationSaved && this.state.customizationSaved === requisition.id) &&
					<small><span className='text-success'>Your changes have been saved. <i className="fa fa-check-circle"></i></span></small>
				}
			</h4>
			
			{ (this.state.showCustomizeOverlay === requisition.omsOrderableItemId) && 
				<Modal
					id={requisition.omsOrderableItemId + '-customization-modal'}
					title={'Customize ' + requisitionTitle}
					confirmAction={omsRequisition && 'Save'}
					cancelAction={'Cancel'}
					onSave={this.onCustomize}
					modalHidden={() => this.showCustomizeOverlay(null)}
					doConfirm={this.state.customizedSelections && this.props.doConfirm}
					>
					{!omsRequisition && 
						<div className='text-center'>{this.state.customizationOverlayStatus}</div>
					}
					{omsRequisition && 
						<PanelCustomization gearRequisition={requisition} omsRequisition={omsRequisition}
							onSelection={this.onSelection} displayPatientInstructions={false} shipToOffice={true}>
							<h4>Select a profile, components, or add-ons to pre-fill the test selection portion of the requisitions.</h4>
						</PanelCustomization>
					}
				</Modal>
			}
			{ omsRequisition && <PanelDetails reqResponses={requisition} requsitionStructure={omsRequisition} heading='' /> }
			{requisition.patientInstructions &&
				<div className="row">
					<div className="col-sm-12">
						<p><strong>Patient Instructions:</strong> {requisition.patientInstructions}</p>
					</div>
				</div>
			}
			{ !hasCustomization && 
				<div>No customization selected</div>
			}

		</div>);
	}

	/**
	 * Creates the JSX component for payment.
	 * Before the order has been confirmed, this component displays the shipping method, price, and credit card form.
	 * After confirmation, this component displays the payment amount and transaction id.
	 *
	 * @return JSX requisition component
	 */
	buildPaymentDisplay = () => {
		if (!this.props.gearOrder.expeditedShippingCharge) {
			return null;
		}

		return (
			<div className='well'>
				<ExpeditedShippingPayment
					userInfo={this.props.userInfo}
					gearOrder={this.props.gearOrder}
					handlePaymentInputChange={this.handlePaymentInputChange}
					onSubmit={this.handleSubmit}
					paymentDetails={this.state.paymentDetails}
					onShippingEdit={this.editShipping}
				/>
			</div>
		);
	}

	render() {
		// Show a different message after information has been confirmed, and hide it if no reqs are on the order
		let confirmAlert;
		if (this.props.gearOrder.requisitions.length === 0) {
			confirmAlert = null;
		} else if (this.state.confirmed) {
			confirmAlert = <AlertMessage>
				<strong>{"You've confirmed that your information is correct. If you need to update your information, please call us at 800-522-4762."}</strong>
			</AlertMessage>;
		} else {
			confirmAlert = <AlertMessage type="info">
				<strong>{"If the data you've entered is correct, please proceed to the next page. Otherwise, please go back and update any incorrect information."}</strong>
				<br />
				If you would like to update quantities or customize test selections you may do so below.
			</AlertMessage>
		}

		return (
			<div>
				<div>
					<div>
						<h2>Confirmation - <small>{"Please confirm that the data you've entered is accurate."}</small></h2>
					</div>
					{confirmAlert}
					<AlertMessage display={this.state.showError === true} type="danger">
						<strong ref={ (ref) => this.errorRef=ref }>{this.state.errorMessage}</strong>
					</AlertMessage>
				</div>
				{this.buildShippingInfoDisplay(this.props.gearOrder)}
				{this.props.gearOrder.requisitions.map((requisition) => this.buildRequisitionDisplay(requisition))}
				<RecommendedProducts
					gearOrder={this.props.gearOrder}
					userInfo={this.props.userInfo}
					addNewRequisition={this.editPanels}
					loadSavedRecommendedProduct={this.loadSavedRecommendedProduct}
				/>
				{this.buildPaymentDisplay()}
				<div className="clearfix"></div>
				<div className={this.state.confirmed ? 'sticky-bottom' : ''}>
					<button disabled={this.state.saveDisabled} className="btn btn-primary radius btn-sm margin-clear pull-right" onClick={this.handleSubmit}>
						Submit
						<i className="pl-10 fa fa-arrow-circle-right"></i>
					</button>
					<button className="btn btn-white radius btn-sm margin-clear" type="button" onClick={this.goBack}>
						<i className="pl-10 fa fa-arrow-circle-left"></i>
						&nbsp; Back
					</button>
				</div>
				{this.state.displayRemoveRequisition && <RemoveRequisition gearOrderId={this.props.gearOrder.id} requisition={this.state.requisitionToRemove} removeRequisitionResponseHandler={this.removeRequisitionResponseHandler} modalHidden={() => this.setState({displayRemoveRequisition: false})}/>}
			</div>
		);
	}
}

// wrap the component in the workflow HOC to get access to common workflow logic
export default withWorkflow(ShipToOfficeConfirmation);
